import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { erroIdentificacao } from "@/lib/refresh";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Recolhivel } from "@/components/Recolhivel";
import { ContatosFamiliares, pendenciasContatos, VAZIO } from "@/components/ContatosFamiliares";
import { Search, UserPlus, Loader2, Eye, MapPin } from "lucide-react";
import { toast } from "sonner";
import { MobileCards, Row } from "@/components/MobileCards";

const EMPTY = {
  nome: "", cpf: "", data_nascimento: "", telefone: "", whatsapp: "", email: "", estado_civil: "",
  cep: "", estado: "", cidade: "", bairro: "", rua: "", numero: "", complemento: "",
  possui_cnpj: false, cnpj: "", nome_comercio: "", tipo_comercio: "", endereco_comercial: "",
  tipo_atividade: "", plataformas_app: "", veiculo: "",
  tempo_atividade: "", telefone_comercial: "", referencias: [], contatos_familiares: [VAZIO(), VAZIO()], observacoes: "", subtipo_atividade: "", possui_garantia: null, garantia_descricao: "",
};

export function ClientForm({ initial, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial, contatos_familiares: initial.contatos_familiares?.length ? initial.contatos_familiares : [VAZIO(), VAZIO()] } : EMPTY);
  const [mostrarErros, setMostrarErros] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const [cepLoading, setCepLoading] = useState(false);
  const buscarCep = async () => {
    const cep = (f.cep || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await r.json();
      if (d.erro) { toast.error("CEP não encontrado"); return; }
      setF((p) => ({ ...p, estado: d.uf || p.estado, cidade: d.localidade || p.cidade, bairro: d.bairro || p.bairro, rua: d.logradouro || p.rua }));
      toast.success("Endereço preenchido pelo CEP");
    } catch { toast.error("Falha ao consultar o CEP"); }
    finally { setCepLoading(false); }
  };
  const abrirMaps = () => {
    const q = [f.rua, f.numero, f.bairro, f.cidade, f.estado, f.cep].filter(Boolean).join(", ") || f.cep;
    if (!q) { toast.error("Informe o CEP ou o endereço"); return; }
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`, "_blank");
  };

  const save = async () => {
    if (!f.nome) { toast.error("Nome é obrigatório"); return; }
    if (!initial?.id || f.cpf || f.data_nascimento) { const eId = erroIdentificacao(f.cpf, f.data_nascimento); if (eId) { toast.error(eId); return; } }
    const pend = pendenciasContatos(f.contatos_familiares, f.telefone, false);
    if (pend.length) { setMostrarErros(true); toast.error(`Contatos familiares: ${pend.join("; ")}`); return; }
    setLoading(true);
    try {
      if (initial?.id) await api.put(`/clients/${initial.id}`, f);
      else await api.post("/clients", f);
      setOpen(false);
      toast.success("Cliente salvo com sucesso!");
      if (!initial) setF(EMPTY);
      setTimeout(() => onSaved && onSaved(), 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar cliente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl slim-scroll" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle>{initial ? "Editar Cliente" : "Novo Cliente"}</DialogTitle></DialogHeader>
        <div className="space-y-2 divide-y">
          <Recolhivel titulo="Dados pessoais" testid="tab-pessoal"><div className="grid md:grid-cols-2 gap-3">
            <F l="Nome completo *"><Input data-testid="cf-nome" value={f.nome} onChange={(e) => set("nome", e.target.value)} /></F>
            <F l="CPF *"><Input data-testid="cf-cpf" inputMode="numeric" value={f.cpf} onChange={(e) => set("cpf", e.target.value)} /></F>
            <F l="Data de nascimento * (validação cadastral)"><Input type="date" data-testid="cf-nascimento" max={new Date().toISOString().slice(0, 10)} value={(f.data_nascimento || "").slice(0, 10)} onChange={(e) => set("data_nascimento", e.target.value)} /></F>
            <F l="Estado civil"><Input value={f.estado_civil} onChange={(e) => set("estado_civil", e.target.value)} /></F>
            <F l="RG"><Input value={f.rg || ""} onChange={(e) => set("rg", e.target.value)} data-testid="cf-rg" /></F>
            <F l="Órgão emissor"><Input value={f.orgao_emissor || ""} onChange={(e) => set("orgao_emissor", e.target.value)} data-testid="cf-orgao" /></F>
            <F l="Profissão / cargo"><Input value={f.profissao || ""} onChange={(e) => set("profissao", e.target.value)} data-testid="cf-profissao" /></F>
            <F l="Telefone"><Input value={f.telefone} onChange={(e) => set("telefone", e.target.value)} /></F>
            <F l="WhatsApp"><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></F>
            <F l="Email"><Input value={f.email} onChange={(e) => set("email", e.target.value)} /></F>
          </div></Recolhivel>
          <Recolhivel titulo="Endereço" testid="tab-endereco"><div className="grid md:grid-cols-2 gap-3">
            <F l="CEP">
              <div className="flex gap-2">
                <Input value={f.cep} onChange={(e) => set("cep", e.target.value)} onBlur={buscarCep} placeholder="00000-000" data-testid="cf-cep" />
                <Button type="button" variant="outline" onClick={buscarCep} disabled={cepLoading} className="shrink-0 gap-1" data-testid="cf-cep-buscar">
                  {cepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Buscar
                </Button>
                <Button type="button" variant="outline" onClick={abrirMaps} className="shrink-0 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50" title="Ver no Google Maps" data-testid="cf-cep-maps">
                  <MapPin className="w-4 h-4" /> Maps
                </Button>
              </div>
            </F>
            <F l="Estado"><Input value={f.estado} onChange={(e) => set("estado", e.target.value)} /></F>
            <F l="Cidade"><Input value={f.cidade} onChange={(e) => set("cidade", e.target.value)} /></F>
            <F l="Bairro"><Input value={f.bairro} onChange={(e) => set("bairro", e.target.value)} /></F>
            <F l="Rua"><Input value={f.rua} onChange={(e) => set("rua", e.target.value)} /></F>
            <F l="Número"><Input value={f.numero} onChange={(e) => set("numero", e.target.value)} /></F>
            <F l="Complemento"><Input value={f.complemento} onChange={(e) => set("complemento", e.target.value)} /></F>
          </div></Recolhivel>
          <Recolhivel titulo="Dados profissionais / comerciais" testid="tab-comercial"><div className="space-y-3">
            <div className="bg-slate-50 rounded-lg px-4 py-3 border space-y-2">
              <span className="text-sm font-medium">Tipo de atividade</span>
              <div className="grid grid-cols-3 gap-2">
                {[["outros", "Outros / CLT"], ["comerciante", "Comerciante"], ["motorista", "Motorista de app"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => set("tipo_atividade", v)} data-testid={`cf-atividade-${v}`}
                    className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${f.tipo_atividade === v ? "bg-onyx text-white border-onyx" : "bg-white hover:border-gold"}`}>{l}</button>
                ))}
              </div>
              {f.tipo_atividade === "outros" && (
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {[["clt", "CLT"], ["outro", "Outro"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => set("subtipo_atividade", v)} data-testid={`cf-subtipo-${v}`} className={`rounded-lg border px-2 py-2 text-xs font-semibold ${f.subtipo_atividade === v ? "bg-onyx text-white border-onyx" : "bg-white hover:border-gold"}`}>{l}</button>
                  ))}
                  {f.subtipo_atividade === "outro" && <div className="col-span-2"><F l="Profissão / atividade"><Input value={f.profissao || ""} onChange={(e) => set("profissao", e.target.value)} data-testid="cf-profissao-outro" /></F></div>}
                </div>
              )}
              {f.tipo_atividade === "motorista" && (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <F l="Plataformas (Uber, 99...)"><Input value={f.plataformas_app || ""} onChange={(e) => set("plataformas_app", e.target.value)} data-testid="cf-plataformas" /></F>
                  <F l="Veículo (modelo/placa)"><Input value={f.veiculo || ""} onChange={(e) => set("veiculo", e.target.value)} data-testid="cf-veiculo" /></F>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3 border">
              <span className="text-sm font-medium">Possui CNPJ / comércio?</span>
              <Switch checked={f.possui_cnpj} onCheckedChange={(v) => set("possui_cnpj", v)} data-testid="cf-possui-cnpj" />
            </div>
            {f.possui_cnpj && (
              <div className="grid md:grid-cols-2 gap-3">
                <F l="CNPJ"><Input value={f.cnpj} onChange={(e) => set("cnpj", e.target.value)} /></F>
                <F l="Nome do comércio"><Input value={f.nome_comercio} onChange={(e) => set("nome_comercio", e.target.value)} /></F>
                <F l="Tipo de comércio"><Input value={f.tipo_comercio} onChange={(e) => set("tipo_comercio", e.target.value)} /></F>
                <F l="Tempo de atividade"><Input value={f.tempo_atividade} onChange={(e) => set("tempo_atividade", e.target.value)} /></F>
                <F l="Endereço comercial"><Input value={f.endereco_comercial} onChange={(e) => set("endereco_comercial", e.target.value)} /></F>
                <F l="Telefone comercial"><Input value={f.telefone_comercial} onChange={(e) => set("telefone_comercial", e.target.value)} /></F>
              </div>
            )}
            <div className="bg-slate-50 rounded-lg px-4 py-3 border space-y-2" data-testid="cf-garantia">
              <div className="flex items-center justify-between"><span className="text-sm font-medium">Possui garantia (bem informado)?</span>
                <div className="flex gap-1">{[[true, "Sim"], [false, "Não"]].map(([v, l]) => <button key={l} type="button" onClick={() => set("possui_garantia", v)} data-testid={`cf-garantia-${l.toLowerCase()}`} className={`rounded-lg border px-3 py-1 text-xs font-semibold ${f.possui_garantia === v ? "bg-onyx text-white border-onyx" : "bg-white"}`}>{l}</button>)}</div></div>
              {f.possui_garantia === true && <F l="Descrição do bem"><Input value={f.garantia_descricao || ""} onChange={(e) => set("garantia_descricao", e.target.value)} data-testid="cf-garantia-descricao" /></F>}
              <p className="text-[11px] text-slate-500">Informativo: não cria termo de garantia nem substitui o fluxo de garantia/assinatura do contrato.</p>
            </div>
          </div></Recolhivel>
          <Recolhivel titulo="Informações adicionais" testid="tab-ref"><div className="space-y-4">
            <F l="Observações (internas — não aparecem no portal do cliente)"><Textarea value={f.observacoes} onChange={(e) => set("observacoes", e.target.value)} data-testid="cf-observacoes" /></F>
            <ContatosFamiliares value={f.contatos_familiares} onChange={(v) => set("contatos_familiares", v)} telefonePrincipal={f.telefone} obrigatorio={false} testid="cf-contato" mostrarErros={mostrarErros} />
            {(f.referencias || []).some((r) => r.nome || r.telefone || r.parentesco) && (
              <details className="text-xs text-slate-600" data-testid="cf-referencias-legado"><summary className="cursor-pointer font-medium">Referências antigas (somente leitura)</summary>
                <ul className="mt-1 list-disc pl-4">{f.referencias.filter((r) => r.nome || r.telefone || r.parentesco).map((r, i) => <li key={i}>{[r.nome, r.parentesco, r.telefone].filter(Boolean).join(" · ")}</li>)}</ul>
              </details>
            )}
          </div></Recolhivel>
        </div>
        <DialogFooter className="pt-3 border-t">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="cf-cancel-btn">Cancelar</Button>
          <Button onClick={save} disabled={loading} data-testid="cf-save-btn" className="bg-onyx hover:bg-black text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ l, children }) => (<div><Label className="text-xs">{l}</Label><div className="mt-1">{children}</div></div>);

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [semNasc, setSemNasc] = useState(null);
  const [soSemNasc, setSoSemNasc] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get(`/clients?q=${encodeURIComponent(q)}${soSemNasc ? "&status=sem_nascimento" : ""}`);
    setClients(data);
    setLoading(false);
    api.get("/clients/sem-nascimento/resumo").then((r) => setSemNasc(r.data)).catch(() => {});
  }, [q, soSemNasc]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-5 fade-up" data-testid="clients-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Clientes</h1>
          <p className="text-slate-500 text-sm">{clients.length} cliente(s) cadastrado(s)</p>
        </div>
        <ClientForm onSaved={load} trigger={
          <Button data-testid="new-client-btn" className="bg-onyx hover:bg-black text-white gap-2"><UserPlus className="w-4 h-4" /> Novo Cliente</Button>
        } />
      </div>

      {semNasc?.total > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3 flex flex-wrap items-center justify-between gap-2" data-testid="clients-sem-nascimento-alerta">
        <span><b>{semNasc.total} cliente(s) sem data de nascimento</b> — a data é usada na validação do cadastro e no acesso antigo ao Portal (CPF + nascimento), quando ainda habilitado para o cliente. O acesso principal é CPF + senha pessoal/provisória. Abra o cadastro e preencha a data real.</span>
        <Button size="sm" variant="outline" className="border-amber-400 text-amber-900" onClick={() => setSoSemNasc((v) => !v)} data-testid="clients-sem-nascimento-filtro">{soSemNasc ? "Mostrar todos" : "Listar afetados"}</Button>
      </div>}

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF, telefone..." className="pl-9" />
      </div>

      <MobileCards items={clients} loading={loading} empty="Nenhum cliente encontrado." testid="client-cards-mobile" render={(c) => (
        <div key={c.id} className="trt-card p-4 space-y-2 cursor-pointer active:bg-slate-50" onClick={() => navigate(`/clientes/${c.id}`)} data-testid={`client-card-${c.id}`}>
          <div className="flex items-start justify-between gap-2"><div className="font-bold break-words leading-snug min-w-0 flex-1">{c.nome}</div><span className="text-xs text-slate-400 shrink-0 capitalize">{c.tipo_atividade || c.profissao || ""}</span></div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            <Row l="CPF" v={<span className="mono">{c.cpf || "—"}</span>} />
            <Row l="Telefone" v={c.telefone} />
            <Row l="Cidade" v={c.cidade} />
            <Row l="Cadastro" v={fmtDate(c.created_at)} />
          </div>
        </div>
      )} />
      <div className="trt-card overflow-hidden hidden lg:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">CPF</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Telefone</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Cidade</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Atividade</th>
              <th className="text-left px-4 py-3 hidden xl:table-cell">Cadastro</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></td></tr>
            ) : clients.length === 0 ? (
              <tr><td colSpan={5} className="py-10 text-center text-slate-400">Nenhum cliente encontrado.</td></tr>
            ) : clients.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)} data-testid={`client-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.nome}</td>
                <td className="px-4 py-3 hidden md:table-cell mono text-slate-500">{c.cpf || "-"}</td>
                <td className="px-4 py-3 hidden md:table-cell">{c.telefone || "-"}</td>
                <td className="px-4 py-3 hidden lg:table-cell">{c.cidade || "-"}</td>
                <td className="px-4 py-3 hidden lg:table-cell capitalize">{c.tipo_atividade || c.profissao || "-"}</td>
                <td className="px-4 py-3 hidden xl:table-cell text-slate-500">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/clientes/${c.id}`); }} data-testid={`view-client-${c.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
